import React, { useState } from 'react';
import PropTypes from 'prop-types';
import { useIntl, FormattedMessage } from 'react-intl';
import { FormGroup } from 'reactstrap';

import { FormField, tcFormValidators, FIELD_TYPES } from '@techchange/components';
import { ApiIntegrationRequired, buildMiroEmbedURL } from '@techchange/integrations';
import { Button, OAUTH_PROVIDERS, OAUTH_TYPES, useLanguageContext } from '@techchange/components';
import { messages } from '../../../../i18n';

import { RESOURCE_TYPES } from '../../../../constants';
import ContentEditor from '../../ContentEditor';
import {
	getInitialLanguage,
	useResourceFormData,
	RESOURCE_FORM_FIELDS,
} from '../../hooks/useResourceFormData';
import { contentBuilderPropTypes, miroDataShape, initialValuesShape } from '../../utils/propTypes';

import BoardSelectorModal from './components/BoardSelectorModal';

import './ContentEditor.scss';

const propTypes = {

	...contentBuilderPropTypes.contentEditor,

	/**
	 * The type of content.
	 */
	type: PropTypes.oneOf([RESOURCE_TYPES.MIRO]).isRequired,

	/**
	 * The initial values for the content editor.
	 */
	initialValues: PropTypes.shape({

		...initialValuesShape,

		/**
		 * The content data.
		 */
		data: PropTypes.shape(miroDataShape),

	}).isRequired,

	/**
	 * API integrations data for the current user.
	 *
	 * NOTE: Only used by those with admin privileges.
	 */
	apiIntegrations: PropTypes.shape({

		data: PropTypes.arrayOf(PropTypes.shape({

			id: PropTypes.string.isRequired,

			oauth_integration_tokens: PropTypes.arrayOf(PropTypes.shape({

				id: PropTypes.string.isRequired,

			})).isRequired,

		})),

		/**
		 * True, if loading the API integrations data.
		 */
		loading: PropTypes.bool.isRequired,

		/**
		 * The error (if any) related to loading the API integrations data.
		 */
		error: PropTypes.bool.isRequired,

		/**
		 * Callback function to refresh the API integrations data.
		 */
		refresh: PropTypes.func.isRequired,

	}),

	/**
	 * Callback function to handle using an API integration.
	 */
	onUseApiIntegration: PropTypes.func.isRequired,

};

const DEFAULT_RESOURCE_CONTENT = {
	[RESOURCE_FORM_FIELDS.TITLE]: '',
	[RESOURCE_FORM_FIELDS.DATA]: {},
};

const getInitialFormData = (props) => {

	const { title, data, width, resourceContents, deletedResourceContents } = props.initialValues;

	return {
		[RESOURCE_FORM_FIELDS.TITLE]: title || '',
		[RESOURCE_FORM_FIELDS.DATA]: data || {},
		[RESOURCE_FORM_FIELDS.LANGUAGE]: getInitialLanguage(props),
		[RESOURCE_FORM_FIELDS.WIDTH]: width,
		resource_contents: resourceContents || [{
			[RESOURCE_FORM_FIELDS.LANGUAGE]: props.defaultLanguage,
			...DEFAULT_RESOURCE_CONTENT,
		}],
		deleted_resource_contents: deletedResourceContents || [],
	};

};

const getInitialInputValidation = () => ({
	[RESOURCE_FORM_FIELDS.TITLE]: [tcFormValidators.required],
	[RESOURCE_FORM_FIELDS.DATA]: {
		board_id: [tcFormValidators.required],
	},
	resource_contents: [tcFormValidators.arrayContainingObjects(RESOURCE_FORM_FIELDS.LANGUAGE, {
		[RESOURCE_FORM_FIELDS.TITLE]: [tcFormValidators.required],
		[RESOURCE_FORM_FIELDS.DATA]: {
			board_id: [tcFormValidators.required],
		},
	})],
});

/**
 * Editor for "miro" resource types. Admins can makes changes to the "title", "data.team_id"
 * and "data.board_id" fields for Miro resources.
 *
 * @param {Object} props  The component props.
 *
 * @returns {Object}  The component to render.
 */
const MiroContentEditor = (props) => {

	const {
		id,
		type,
		apiIntegrations,
		onUseApiIntegration,
		onCancel,
	} = props;

	const intl = useIntl();

	const { preferredLanguage, defaultLanguage } = useLanguageContext();

	const formProps = {
		...props,
		userLanguage: preferredLanguage,
		defaultLanguage,
	};

	const { formData, isSubmitting, inputValidation, actions } = useResourceFormData(
		() => getInitialFormData(formProps),
		getInitialInputValidation,
		{ ...formProps, defaultResourceContent: DEFAULT_RESOURCE_CONTENT }
	);

	// Check if we need to show the Miro board selector UI
	const [showBoardSelector, setShowBoardSelector] = useState(false);

	/**
	 * Handles Miro board selection.
	 *
	 * @param {Object} boardData  Data for the selected board.
	 * @param {String} boardData.id  Unique ID for the selected board.
	 * @param {String} boardData.name  Name of the selected board.
	 * @param {Object} boardData.team  Associated team data.
	 * @param {String} boardData.team.id  Unique ID for the associated team.
	 */
	const handleSelectBoard = (boardData) => {

		// TODO: Should we hardcode additional board data here???

		actions.updateField(RESOURCE_FORM_FIELDS.BULK, {
			[RESOURCE_FORM_FIELDS.DATA]: {
				board_id: boardData.id,
				team_id: boardData.team.id,
			},
			[RESOURCE_FORM_FIELDS.TITLE]: boardData.name,
		});

		setShowBoardSelector(false);

	};

	// Check if the organization supports an active Miro API integration
	const miroIntegration =
		apiIntegrations?.data &&
		apiIntegrations.data.find((integration) =>
			integration.type === OAUTH_TYPES.API &&
			integration.provider === OAUTH_PROVIDERS.MIRO &&
			integration.active
		);

	const activeTranslation = formData.resource_contents
		.find((rContent) => rContent.language === formData.language);

	const activeInputValidation = inputValidation?.resource_contents?.data?.[formData.language];

	const hasSelectedBoard = activeTranslation?.[RESOURCE_FORM_FIELDS.DATA]?.board_id;
	const isBoardIDInvalid = activeInputValidation?.[RESOURCE_FORM_FIELDS.DATA]?.board_id.invalid;

	const formFields = activeTranslation && (
		<>
			<FormField
				type={FIELD_TYPES.TEXT}
				name={RESOURCE_FORM_FIELDS.TITLE}
				value={activeTranslation[RESOURCE_FORM_FIELDS.TITLE]}
				label={intl.formatMessage(messages.TC_COMPONENTS_RESOURCE_CONTENT_BUILDER_MIRO_FIELD_LABEL_TEXT_TITLE)}
				placeholder={intl.formatMessage(messages.TC_COMPONENTS_RESOURCE_CONTENT_BUILDER_MIRO_FIELD_LABEL_TEXT_TITLE)}
				isInvalid={activeInputValidation?.[RESOURCE_FORM_FIELDS.TITLE]?.invalid}
				error={activeInputValidation?.[RESOURCE_FORM_FIELDS.TITLE]?.error}
				isEditing
				isRequired
				onChange={actions.updateField} />
			{!hasSelectedBoard &&
				<FormGroup>
					<Button
						className={isBoardIDInvalid ? 'tc-material-input-invalid' : ''}
						color={isBoardIDInvalid ? 'danger' : 'neutral'}
						outline
						onClick={() => setShowBoardSelector(true)}>
						<FormattedMessage
							{...messages.TC_COMPONENTS_RESOURCE_CONTENT_BUILDER_MIRO_BUTTON_TEXT_BOARD} />
					</Button>
					{isBoardIDInvalid &&
						<div className="invalid-feedback">
							{activeInputValidation?.[RESOURCE_FORM_FIELDS.DATA]?.board_id.error}
						</div>
					}
				</FormGroup>
			}
			{hasSelectedBoard &&
				<Button
					className="tc-content-builder-miro-editor-iframe-btn mb-3"
					aria-label={intl.formatMessage(messages.TC_COMPONENTS_RESOURCE_CONTENT_BUILDER_MIRO_EMBED_PREVIEW_BUTTON_ARIA_LABEL)}
					color="neutral"
					outline
					onClick={() => setShowBoardSelector(true)}>
					<div className="tc-content-builder-miro-editor-iframe">
						<iframe
							src={buildMiroEmbedURL(activeTranslation[RESOURCE_FORM_FIELDS.DATA].board_id)}
							title={activeTranslation[RESOURCE_FORM_FIELDS.TITLE]}
							frameBorder="0"
							scrolling="no"
							tabIndex="-1"
							style={{ pointerEvents: 'none' }}
							allowFullScreen />
						<div className="tc-content-builder-miro-editor-iframe-overlay">
							{intl.formatMessage(messages.TC_COMPONENTS_RESOURCE_CONTENT_BUILDER_MIRO_EMBED_PREVIEW_BUTTON_ARIA_LABEL)}
						</div>
					</div>
				</Button>
			}
		</>
	);

	return (
		<ApiIntegrationRequired
			requiredIntegration={miroIntegration}
			apiIntegrations={apiIntegrations}
			isConnectionRequired={showBoardSelector}
			render={({ apiIntegrationAccountInfo }) => (
				<>
					{showBoardSelector && apiIntegrationAccountInfo.connected &&
						<BoardSelectorModal
							integration={miroIntegration}
							onUseApiIntegration={onUseApiIntegration}
							onCancel={() => setShowBoardSelector(false)}
							onSelectBoard={handleSelectBoard} />
					}
					<ContentEditor
						id={id}
						type={type}
						formData={formData}
						isSubmitting={isSubmitting}
						inputValidation={inputValidation}
						actions={actions}
						formFields={formFields}
						onCancel={onCancel} />
				</>
			)}
			onCancel={() => setShowBoardSelector(false)}
			onUseAPI={onUseApiIntegration} />
	);

};

MiroContentEditor.propTypes = propTypes;

export default MiroContentEditor;
